import {
  PackageWithAdditionalInfo,
  missingProducts,
  packageProductValue,
  productCategories,
} from 'lib/const/PackageAdditionalInfo';
import { IPackageProduct } from 'lib/context';
import { theme } from 'lib/style';
import { Dictionary, keyBy } from 'lodash';
import React from 'react';
import { MdCheck, MdClose } from 'react-icons/md';
import styled, { css } from 'styled-components/macro';
import { Card } from '.';

const Wrapper = styled.div`
  padding: 64px 0;
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  color: ${theme.palette.gray100};
  text-align: center;
  margin-bottom: 56px;
`;

type ComparisonHeaderWrapperProps = {
  fixedHeader?: boolean;
};
const ComparisonHeaderWrapper = styled.div<ComparisonHeaderWrapperProps>`
  ${({ fixedHeader }) =>
    fixedHeader &&
    css`
      background: ${theme.palette.white};
      position: fixed;
      z-index: 99;
      top: 64px;
      left: 0;
      right: 0;
      box-shadow: 0px 0px 2px rgba(66, 79, 104, 0.08),
        0px 4px 8px rgba(66, 79, 104, 0.03);
      ${ComparisonHeader} {
        margin: 0 auto;
        width: 1008px;
      }
    `}
`;
const ComparisonHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  column-gap: 60px;
  padding: 16px 0;
`;

const ComparisonHeaderLabel = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
  color: ${theme.palette.gray60};
  margin-right: auto;
`;

const ProductsWrapper = styled.div``;

const ProductRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  column-gap: 60px;
  border-top: 1px solid ${theme.palette.lightgray40};
  padding: 16px 0;
`;

const CategoryWrapper = styled.div``;

const CategoryTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: ${theme.palette.gray100};
  display: flex;
  align-items: center;
  border-top: 1px solid ${theme.palette.lightgray40};
  padding: 16px 0;
`;

const ProductTitle = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${theme.palette.gray100};
  margin-right: auto;
`;

const PackageProductWrapper = styled.div`
  width: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

type PackageProductValueProps = {
  unlimited?: boolean;
};
const PackageProductValue = styled.div<PackageProductValueProps>`
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  padding: 2px 8px;
  border-radius: 6px;
  color: ${theme.palette.blue80};
  background: ${theme.palette.blue02};
  ${({ unlimited }) =>
    unlimited &&
    css`
      color: ${theme.palette.covideoOrange100};
      background: ${theme.palette.covideoOrange05};
    `}
`;
const PackageProductYes = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: ${theme.palette.white};
  background: ${theme.palette.covideoOrange100};
`;
const PackageProductNo = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: ${theme.palette.red100};
  background: ${theme.palette.red05};
`;

type Props = {
  packages: PackageWithAdditionalInfo[];
  billingCycle: string;
  currentPackage: number;
  fixedHeader: boolean;
  onClickPackage: (id: number, cancel?: boolean) => void;
  onContactUs: (packageTitle: string) => void;
};

export const ComparePackages = ({
  packages,
  currentPackage,
  onClickPackage,
  billingCycle,
  onContactUs,
  fixedHeader,
}: Props) => {
  const productCategoriesArray = Object.values(productCategories);
  let products = {} as Dictionary<IPackageProduct>;
  const packagesData = packages.map((p: PackageWithAdditionalInfo) => {
    const additionalProductsDetails = p.additionalProducts.map(
      (productId: number) => {
        let productDetails = missingProducts[productId];
        if (packageProductValue?.[productId]?.[p.id]) {
          productDetails = {
            ...productDetails,
            ...packageProductValue[productId][p.id],
          };
        }
        return productDetails;
      }
    );
    const newProducts = {
      ...keyBy(p.products, 'id'),
      ...keyBy(additionalProductsDetails, 'id'),
    };
    //@ts-ignore
    products = { ...products, ...newProducts };
    return {
      ...p,
      products: newProducts,
    };
  });
  return (
    <Wrapper>
      <Title>Compare all products</Title>
      <ComparisonHeaderWrapper fixedHeader={fixedHeader}>
        <ComparisonHeader>
          <ComparisonHeaderLabel>Feature</ComparisonHeaderLabel>
          {packages.map((packageDetails, i) => {
            if (packageDetails.isHidden) {
              return <React.Fragment key={i}></React.Fragment>;
            }
            return (
              <Card
                key={i}
                packageDetails={packageDetails}
                currentPackage={currentPackage}
                onClickPackage={onClickPackage}
                billingCycle={billingCycle}
                onContactUs={onContactUs}
                simplified={true}
              />
            );
          })}
        </ComparisonHeader>
      </ComparisonHeaderWrapper>
      <ProductsWrapper>
        {productCategoriesArray.map((productCategory, i) => (
          <CategoryWrapper key={i}>
            <CategoryTitle>{productCategory.title}</CategoryTitle>
            {productCategory.products.map((f, i) => (
              <ProductRow key={i}>
                <ProductTitle>{products?.[f]?.name || ''}</ProductTitle>
                {packagesData.map((p, i) => {
                  if (p.isHidden) {
                    return <React.Fragment key={i}></React.Fragment>;
                  }
                  return (
                    <PackageProductWrapper key={i}>
                      {p?.products?.[f]?.value ? (
                        <PackageProductValue
                          unlimited={p?.products?.[f]?.value === 'Unlimited'}
                        >
                          {p?.products?.[f]?.value}
                        </PackageProductValue>
                      ) : p?.products?.[f] ? (
                        <PackageProductYes>
                          <MdCheck size={12} />
                        </PackageProductYes>
                      ) : (
                        <PackageProductNo>
                          <MdClose size={12} />
                        </PackageProductNo>
                      )}
                    </PackageProductWrapper>
                  );
                })}
              </ProductRow>
            ))}
          </CategoryWrapper>
        ))}
      </ProductsWrapper>
    </Wrapper>
  );
};
